"use client";

import { useState, useEffect, useRef } from "react";
import { MapPin, Search, X, Loader2, Plus } from "lucide-react";

interface Area {
  name: string;
  lat: number;
  lng: number;
  type?: string;
  postcode?: string;
}

interface AreaSearchProps {
  selectedAreas: Area[];
  onAdd: (area: Area) => void;
  onRemove: (name: string) => void;
}

export default function AreaSearch({ selectedAreas, onAdd, onRemove }: AreaSearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query.length < 3) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(query)}&limit=5`);
        const data = await res.json();
        setResults(data);
        setIsOpen(true);
      } catch (e) {
        console.error("Nominatim error", e);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="space-y-4">
      <div className="relative" ref={dropdownRef}>
        <div className="relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={18} />
          <input
            type="text"
            placeholder="Search city, town, or area worldwide..."
            className="w-full rounded-xl border border-slate-200 bg-white py-3.5 pl-11 pr-4 text-sm outline-none ring-blue-100 transition-all focus:border-blue-600 focus:ring-4"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.length >= 3 && setIsOpen(true)}
          />
          {loading && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <Loader2 className="animate-spin text-slate-400" size={18} />
            </div>
          )}
        </div>

        {isOpen && results.length > 0 && (
          <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl animate-in fade-in slide-in-from-top-2">
            <div className="max-h-64 overflow-y-auto">
              {results.map((r, i) => (
                <button
                  key={i}
                  type="button"
                  className="flex w-full items-start gap-3 p-4 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                  onClick={() => {
                    onAdd({
                      name: r.display_name,
                      lat: parseFloat(r.lat),
                      lng: parseFloat(r.lon),
                      type: r.type,
                      postcode: r.address?.postcode,
                    });
                    setQuery("");
                    setIsOpen(false);
                  }}
                >
                  <MapPin className="mt-0.5 shrink-0 text-slate-400" size={16} />
                  <div>
                    <p className="text-sm font-medium text-slate-900 leading-tight">{r.display_name}</p>
                    <p className="mt-0.5 text-xs text-slate-400 uppercase tracking-wider font-semibold">{r.type || "location"}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedAreas.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedAreas.map((area, i) => (
            <div
              key={i}
              className="flex items-center gap-2 rounded-full bg-blue-50 border border-blue-100 px-3 py-1.5 text-blue-700 shadow-sm transition-all hover:border-blue-200"
            >
              <span className="text-xs font-medium max-w-[200px] truncate">{area.name}</span>
              <button
                type="button"
                onClick={() => onRemove(area.name)}
                className="rounded-full p-0.5 hover:bg-blue-100 transition-colors"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedAreas.length === 0 && !loading && !query && (
        <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center bg-slate-50/50">
          <MapPin className="mx-auto text-slate-300 mb-2" size={24} />
          <p className="text-sm text-slate-500">No areas added yet. Search to add your service locations.</p>
        </div>
      )}
    </div>
  );
}
